import { useCallback, useEffect, useState } from 'react';
import type { DependencyStatus, HealthResponse } from '@firegame/shared';
import { fetchHealth } from '../api/health.js';
import { ApiClientError } from '../api/client.js';
import { formatDuration, t } from '../i18n/index.js';
import { Button } from './Button.js';
import './ServiceStatusCard.css';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; health: HealthResponse }
  | { kind: 'error'; message: string };

function dependencyLabel(dependency: DependencyStatus): string {
  const labels = t().status;
  switch (dependency.status) {
    case 'up':
      return labels.dependencyUp;
    case 'down':
      return labels.dependencyDown;
    case 'disabled':
      return labels.dependencyDisabled;
  }
}

/**
 * Carte d'etat des services.
 *
 * Elle sert de verification de bout en bout : si elle affiche « operationnel »,
 * c'est que le navigateur, le proxy Vite ou Nginx, et l'API communiquent
 * correctement.
 */
export function ServiceStatusCard() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    fetchHealth(controller.signal)
      .then((health) => {
        if (active) setState({ kind: 'ready', health });
      })
      .catch((error: unknown) => {
        if (!active || controller.signal.aborted) return;
        const message = error instanceof ApiClientError ? error.message : t().errors.unexpected;
        setState({ kind: 'error', message });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [reloadToken]);

  // La remise a l'etat « chargement » se fait ici, dans un gestionnaire
  // d'evenement, et non dans l'effet : cela evite un rendu en cascade.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((token) => token + 1);
  }, []);

  const labels = t().status;

  return (
    <section className="fg-status" aria-live="polite">
      <h2 className="fg-status__title">{labels.title}</h2>

      {state.kind === 'loading' && <p className="fg-status__line">{labels.checking}</p>}

      {state.kind === 'error' && (
        <div className="fg-status__error">
          <p className="fg-status__badge fg-status__badge--down">{labels.offline}</p>
          <p className="fg-status__line">{state.message}</p>
          <Button variant="secondary" size="sm" onClick={retry}>
            {labels.retry}
          </Button>
        </div>
      )}

      {state.kind === 'ready' && (
        <div>
          <p
            className={`fg-status__badge ${
              state.health.status === 'ok' ? 'fg-status__badge--up' : 'fg-status__badge--degraded'
            }`}
          >
            {state.health.status === 'ok' ? labels.online : labels.degraded}
          </p>

          <dl className="fg-status__grid">
            <div>
              <dt>{labels.version}</dt>
              <dd>{state.health.version}</dd>
            </div>
            <div>
              <dt>{labels.engine}</dt>
              <dd>v{state.health.engineVersion}</dd>
            </div>
            <div>
              <dt>{labels.environment}</dt>
              <dd>{state.health.environment}</dd>
            </div>
            <div>
              <dt>{labels.uptime}</dt>
              <dd>{formatDuration(state.health.uptimeSeconds)}</dd>
            </div>
          </dl>

          <h3 className="fg-status__subtitle">{labels.dependencies}</h3>
          <ul className="fg-status__dependencies">
            {state.health.dependencies.map((dependency) => (
              <li key={dependency.name}>
                <span
                  className={`fg-status__dot fg-status__dot--${dependency.status}`}
                  aria-hidden="true"
                />
                <span className="fg-status__dependency-name">{dependency.name}</span>
                <span className="fg-status__dependency-state">{dependencyLabel(dependency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
